import { useEffect, useState } from "react";
import { useReducedMotion } from "framer-motion";
import { PhoneOutgoing } from "lucide-react";
import { cn } from "@wa/ui";
import { formatPhone } from "../../../lib/phone";
import type { LivePhase } from "../../../lib/aiLive";
import { dialledChars } from "./aiLive";

/** One more character of the number every this many milliseconds. */
const DIGIT_MS = 70;

/**
 * The number, "as if the AI were dialling it by hand": while the call is being
 * placed it appears a character at a time, then it stands whole and rings.
 *
 * The animation starts when this component first sees the call dialling, not
 * at the call's start time: a dock opened mid-dial shows the rest of the number
 * arriving rather than jumping. Reduced motion shows the number at once and
 * drops the ring's pulse.
 */
export function AiCallDialing({ number, phase }: { number: string; phase: LivePhase }) {
  const reduced = useReducedMotion() ?? false;
  const shown = formatPhone(number);
  const [since] = useState(() => Date.now());
  const [now, setNow] = useState(() => Date.now());
  const dialling = phase === "dialing";
  const chars = dialling ? dialledChars(shown, now - since, DIGIT_MS, reduced) : shown.length;

  useEffect(() => {
    if (!dialling || reduced || chars >= shown.length) return;
    const timer = setTimeout(() => setNow(Date.now()), DIGIT_MS);
    return () => clearTimeout(timer);
  }, [dialling, reduced, chars, shown.length, now]);

  return (
    <span className={cn("crm-ailive-num", phase === "ringing" && !reduced && "is-ringing")}>
      <PhoneOutgoing className="size-3.5 shrink-0" aria-hidden="true" />
      {/* The whole number for a screen reader at once; the typing is for the eye. */}
      <span className="sr-only">{shown}</span>
      <span aria-hidden="true" className="tabular-nums">
        {shown.slice(0, chars)}
        {chars < shown.length ? <span className="crm-ailive-caret" /> : null}
      </span>
    </span>
  );
}
